/**
 * Date utilities for values that represent a calendar day (no time-of-day),
 * like `active_jobs.scheduled_date` which comes from a Postgres DATE column.
 *
 * Why this file exists: JavaScript's built-in `new Date('2026-04-23')`
 * parses date-only strings as UTC midnight, so running
 * `new Date('2026-04-23').toLocaleDateString()` in Eastern Time prints
 * "4/22/2026" because 2026-04-23T00:00:00Z is 2026-04-22 20:00 EDT. Every
 * function here is timezone-safe: the day the database stores is the day
 * the user sees.
 */

// Extract the YYYY-MM-DD portion from any date-ish value the backend might
// return: plain "2026-04-23", an ISO timestamp like "2026-04-23T00:00:00.000Z",
// or a Date/number.
const extractYmd = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return {
        year: parseInt(match[1], 10),
        month: parseInt(match[2], 10),
        day: parseInt(match[3], 10),
      };
    }
  }
  // Fallback: treat as a real timestamp and pull local y/m/d
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
};

/**
 * Format a calendar-day value for display.
 *
 * @param {string|Date} value  DATE column value, e.g. "2026-04-23"
 * @param {string} [locale]    BCP-47 locale (defaults to browser default)
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string}           Formatted date, or "" if value is invalid/empty.
 */
export const formatScheduledDate = (value, locale, options) => {
  const ymd = extractYmd(value);
  if (!ymd) return '';
  // Build a Date in LOCAL time (month is zero-indexed) so the day doesn't
  // shift across timezones.
  const localDate = new Date(ymd.year, ymd.month - 1, ymd.day);
  return localDate.toLocaleDateString(locale, options);
};

/**
 * Returns true if the given calendar-day value represents today in the
 * user's local timezone. Safe against the same UTC-vs-local drift as above.
 */
export const isScheduledDateToday = (value) => {
  const ymd = extractYmd(value);
  if (!ymd) return false;
  const now = new Date();
  return (
    ymd.year === now.getFullYear() &&
    ymd.month === now.getMonth() + 1 &&
    ymd.day === now.getDate()
  );
};

/**
 * Returns today's date as a "YYYY-MM-DD" string in the user's local
 * timezone (not UTC). Useful when comparing against a DATE column.
 * Pass a Date to get that day's local "YYYY-MM-DD" instead of today's.
 */
export const todayLocalYmd = (now = new Date()) => {
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
};
